// Environment helper - React implementation of Angular environmentHelper
// Provides access to Magellan configuration values

class EnvironmentHelper {
  constructor() {
    // Initialize default values if $m is not available (for development)
    if (typeof window !== "undefined" && typeof window.$m === "undefined") {
      window.$m = {
        magellanConfig: {
          ApplicationHost: this.getBaseHost(), // Use dynamic base host, not hardcoded
          ProductSummaryEndpoint: "",
          SharedListEndpoint: "/shared-list/",
          SharedListDetailUrl: "/shared-list/",
          UserTypeEndpoint: "/api/store/user-type",
          CatalogDomain: "",
          DefaultProductTradeName: "",
          PageTitle: "",
          PdfEndpoint: "",
          cacheControlTimestamp: "",
          applicationBasePath: "",
          magellanPricingApiPath: "",
          primaryAntibodiesEndpoint: "",
          ssoUrl: "/auth/login", // Simple login URL, not Gigya identity service
        },
      };
    }
  }

  getValue(key) {
    if (
      typeof window === "undefined" ||
      !window.$m ||
      !window.$m.magellanConfig
    ) {
      console.warn(
        `Environment helper: $m.magellanConfig not available, using defaults`
      );
      return this.getDefaultValue(key);
    }

    const config = window.$m.magellanConfig;
    // ALWAYS use dynamic base host detection, don't trust config.ApplicationHost
    // This ensures QA environment uses QA URLs
    const baseHost = this.getBaseHost();

    switch (key) {
      case "shared_list_endpoint":
        return this.buildFullUrl(baseHost, config.SharedListEndpoint);
      case "shared_list_detail_url":
        return this.buildFullUrl(baseHost, config.SharedListDetailUrl);
      case "user_type_endpoint":
        return this.buildFullUrl(baseHost, config.UserTypeEndpoint);
      case "sso_url":
        // Don't use identity service URL, use simple auth/login pattern
        // This will be handled by redirectToLogin() which checks Magellan config
        return config.ssoUrl || "/auth/login";
      case "catalog_domain":
        return config.CatalogDomain;
      case "domain":
        return baseHost;
      case "product_summary_endpoint":
        return config.ProductSummaryEndpoint;
      case "pdf_endpoint":
        return config.PdfEndpoint;
      case "country_cookie":
        return this.getCookie("CK_ISO_CODE") || "us";
      case "language_cookie":
        return this.getCookie("CK_LANG_CODE") || "en";
      default:
        console.warn(`Environment helper: Unknown key '${key}'`);
        return null;
    }
  }

  getDefaultValue(key) {
    const baseHost = this.getBaseHost();
    const defaults = {
      shared_list_endpoint: `${baseHost}/shared-list/`,
      shared_list_detail_url: `${baseHost}/shared-list/`,
      user_type_endpoint: `${baseHost}/api/store/user-type`,
      sso_url: "/auth/login", // Simple login URL pattern
      catalog_domain: window.location.origin,
      country_cookie: "us",
      language_cookie: "en",
    };

    return defaults[key] || null;
  }

  buildFullUrl(baseHost, path) {
    if (!path) return baseHost;

    // If path is already a full URL, return it as is
    if (path.startsWith("http://") || path.startsWith("https://")) {
      return path;
    }

    // Ensure baseHost doesn't end with slash and path starts with slash
    const cleanBaseHost = baseHost.endsWith("/")
      ? baseHost.slice(0, -1)
      : baseHost;
    const cleanPath = path.startsWith("/") ? path : `/${path}`;

    return `${cleanBaseHost}${cleanPath}`;
  }

  // Get the correct identity service URL based on environment
  getIdentityServiceUrl() {
    // Define the identity service URL with proper gig_client_id
    const identityBaseUrl =
      "https://identity.thermofisher.com/account-center/simplified-username.html";
    const gigClientId = "QuibnISfymD5KJf_H8i4mnPz"; // Production client ID

    // For now, use the same identity service for all environments
    // This could be environment-specific if needed in the future
    return `${identityBaseUrl}?gig_client_id=${gigClientId}`;
  }

  getCookie(name) {
    if (typeof document === "undefined") return null;

    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop().split(";").shift();
    }
    return null;
  }

  getLocaleCountry() {
    return this.getValue("country_cookie");
  }

  getLocaleLanguage() {
    return this.getValue("language_cookie");
  }

  debugModeEnabled() {
    return this.getCookie("debug_mode") === "1";
  }

  // Get the current environment's base host
  getBaseHost() {
    if (typeof window === "undefined") return "https://www.thermofisher.com";

    const hostname = window.location.hostname;

    // Determine environment based on hostname
    if (hostname.includes("localhost") || hostname.includes("127.0.0.1")) {
      return "https://www.thermofisher.com"; // Default to production for local development
    } else if (hostname.includes("qa") || hostname.includes("test")) {
      return `https://${hostname}`; // Use QA environment
    } else if (hostname.includes("dev")) {
      return `https://${hostname}`; // Use dev environment
    } else {
      return "https://www.thermofisher.com"; // Production
    }
  }
}

// Create singleton instance
const environmentHelper = new EnvironmentHelper();

export default environmentHelper;
